import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import { HOOS_JR_SYSTEM } from '../codeSystem/hoosJr';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  type ItemFraming,
  ORDINAL_VALUE_URL,
  OPTION_PREFIX_URL,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * HOOS, JR. (Hip dysfunction and Osteoarthritis Outcome Score for Joint Replacement) — a six-item,
 * CMMI-required hip outcome instrument. Item structure and LOINC coding follow the LOINC HOOS, JR.
 * panel (LOINC 82316-1), published here under our own canonical `url` and version, mirroring the
 * other scored screening surveys ({@link ./phq9}, {@link ./ipss}) — they share the scored-survey
 * machinery in {@link ./scoredSurvey}.
 *
 * All six items are required and scored 0–4 (None / Mild / Moderate / Severe / Extreme). HOOS, JR.
 * has no LOINC answer codes, so — like the Daily Mood Scale ({@link ./moodScale}) and PGIC
 * ({@link ./pgic}) — its answer options live on a minted in-house CodeSystem (`hoos-jr`) rather than
 * LOINC. The total-score result code, however, is a real LOINC (`82323-7`).
 *
 * SCORING is two-stage. The contained Observation template (carrying the total-score LOINC plus
 * `sdc-questionnaire-templateExtract` annotations, identical to PHQ-9) yields the **raw item sum
 * (0–24)** server-side via Medplum's SDC `QuestionnaireResponse/$extract` — verified by
 * `src/__tests__/integration/hoosJrExtractScoring.test.ts`. The validated HOOS, JR. then maps that
 * raw sum to a **0–100 interval score** (100 = perfect hip health) through a Rasch-analysis lookup
 * table. That mapping is non-linear, so it cannot be a FHIRPath formula; the submission service
 * applies {@link convertHoosJrRawScoreToInterval} to the extracted value before persisting the
 * score Observation. The persisted total-score Observation (LOINC `82323-7`) therefore carries the
 * interval score; the raw answers remain reachable via `Observation.derivedFrom → QR`.
 *
 * The six items split into two groups, each with its own framing prompt (per-item framing extension,
 * the PHQ-9 v4 pattern): a pain group (Q1–Q2) and a function/difficulty group (Q3–Q6). Rendering as
 * flat items with per-item framing — rather than FHIR `group` items — keeps the existing
 * QuestionnaireForm and the `$extract` sum unchanged.
 *
 * Source: the HOOS, JR. FHIR survey JSON attached to the originating issue (English + Spanish),
 * whose item structure and LOINC coding match the LOINC HOOS, JR. panel (https://loinc.org/82316-1).
 * The English item text is the standard validated HOOS, JR. wording. There is no validated Spanish
 * HOOS, JR.; the inline Spanish translations on the `_`-sibling elements are an unvalidated
 * machine-assisted translation supplied with the issue and must be reviewed before go-live.
 */

/** HOOS, JR. total (interval) score. Distinct from the panel LOINC (82316-1) on `Questionnaire.code`. */
export const HOOS_JR_TOTAL_SCORE_LOINC = '82323-7';

/** Highest possible raw item sum: six items × ordinal 4. */
export const HOOS_JR_RAW_SCORE_MAX = 24;

/**
 * HOOS, JR. interval-score lookup, indexed by raw item sum (0–24). Each raw sum maps to a 0–100
 * interval score (100 = perfect hip health) derived from Rasch analysis — a fixed, non-linear table,
 * not a formula. Source: the HSS HOOS, JR. scoring instructions attached to the originating issue.
 */
const HOOS_JR_INTERVAL_SCORE_BY_RAW = [
  100.0, 92.34, 85.257, 80.55, 76.776, 73.472, 70.426, 67.516, 64.664, 61.815, 58.93, 55.985, 52.965, 49.858, 46.652,
  43.335, 39.902, 36.363, 32.735, 29.009, 25.103, 20.805, 15.633, 8.104, 0.0,
] as const;

/**
 * Converts a HOOS, JR. raw item sum (integer 0–24) to its 0–100 interval score via the Rasch lookup
 * table. The submission service calls this on the value `$extract` derives so the persisted
 * total-score Observation carries the reported interval score. Throws if the raw score is not an
 * integer in 0–{@link HOOS_JR_RAW_SCORE_MAX} (the six required 0–4 items can only sum to that range).
 */
export function convertHoosJrRawScoreToInterval(rawScore: number): number {
  const interval = Number.isInteger(rawScore) ? HOOS_JR_INTERVAL_SCORE_BY_RAW[rawScore] : undefined;
  if (interval === undefined) {
    throw new RangeError(`HOOS, JR. raw score must be an integer 0–${HOOS_JR_RAW_SCORE_MAX}, got ${rawScore}`);
  }
  return interval;
}

/** Pain group (Q1–Q2) framing, shown above each pain item. */
const painFraming: ItemFraming = {
  en: 'What amount of hip pain have you experienced the last week during the following activities?',
  es: '¿Cuánto dolor de cadera ha experimentado durante la última semana al realizar las siguientes actividades?',
};

/** Function/difficulty group (Q3–Q6) framing, shown above each function item. */
const functionFraming: ItemFraming = {
  en: 'For each of the following activities please indicate the degree of difficulty you have experienced in the last week due to your hip.',
  es: 'Para cada una de las siguientes actividades, indique el grado de dificultad que ha experimentado durante la última semana debido a su cadera.',
};

/**
 * One option on the HOOS, JR. None–Extreme scale: the minted answer coding plus the `optionPrefix`
 * (the leading "0"/"1"/… badge) and `ordinalValue` (the scored weight) extensions. Mirrors
 * {@link frequencyAnswerOption} but on the in-house `hoos-jr` system rather than LOINC.
 */
const hoosJrAnswerOption = (
  ordinal: number,
  code: string,
  display: string,
  displayEs: string,
): QuestionnaireItemAnswerOptionWithTranslations => ({
  extension: [
    { url: OPTION_PREFIX_URL, valueString: String(ordinal) },
    { url: ORDINAL_VALUE_URL, valueDecimal: ordinal },
  ],
  valueCoding: { system: HOOS_JR_SYSTEM, code, display, _display: withEs(displayEs) },
});

const hoosJrAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  hoosJrAnswerOption(0, 'none', 'None', 'Ninguno/a'),
  hoosJrAnswerOption(1, 'mild', 'Mild', 'Leve'),
  hoosJrAnswerOption(2, 'moderate', 'Moderate', 'Moderado/a'),
  hoosJrAnswerOption(3, 'severe', 'Severe', 'Grave'),
  hoosJrAnswerOption(4, 'extreme', 'Extreme', 'Extremo/a'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/82317-9',
    '82317-9',
    'Going up or down stairs',
    'Going up or down stairs',
    'Subir o bajar escaleras',
    true,
    hoosJrAnswerOptions,
    painFraming,
  ),
  symptomItem(
    '/82318-7',
    '82318-7',
    'Walking on an uneven surface',
    'Walking on an uneven surface',
    'Caminar sobre una superficie irregular',
    true,
    hoosJrAnswerOptions,
    painFraming,
  ),
  symptomItem(
    '/82319-5',
    '82319-5',
    'Rising from sitting',
    'Rising from sitting',
    'Levantarse de una silla',
    true,
    hoosJrAnswerOptions,
    functionFraming,
  ),
  symptomItem(
    '/82320-3',
    '82320-3',
    'Bending to floor/pick up an object',
    'Bending to floor/pick up an object',
    'Agacharse al piso / recoger un objeto',
    true,
    hoosJrAnswerOptions,
    functionFraming,
  ),
  symptomItem(
    '/82321-1',
    '82321-1',
    'Lying in bed (turning over, maintaining hip position)',
    'Lying in bed (turning over, maintaining hip position)',
    'Acostarse en la cama (darse vuelta, mantener la posición de la cadera)',
    true,
    hoosJrAnswerOptions,
    functionFraming,
  ),
  symptomItem(
    '/82322-9',
    '82322-9',
    'Sitting',
    'Sitting',
    'Estar sentado/a',
    true,
    hoosJrAnswerOptions,
    functionFraming,
  ),
];

const hoosJrScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'hoos-jr-score-tmpl',
  totalScoreCode: HOOS_JR_TOTAL_SCORE_LOINC,
  display: 'HOOS, JR.',
  scoredItems: scoredSymptomItems,
});

const URL = 'https://liza.health/fhir/questionnaire/hoos-jr';

export const hoosJrQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('hoos-jr-score-tmpl')],
  contained: [hoosJrScoreObservationTemplate],
  url: URL,
  // No standard OID — mirror the canonical url as a FHIR identifier so re-deploys upsert
  // (identifier-based idempotency) rather than duplicate.
  identifier: [{ system: 'urn:ietf:rfc:3986', value: URL }],
  version: '1',
  name: 'HOOSJR',
  language: 'en',
  title: 'HOOS, JR.',
  _title: withEs('HOOS, JR.'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-30',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. © 2020 Hospital for Special Surgery. Hospital for Special Surgery, HSS and the HSS logo are trademarks or registered trademarks of Hospital for Special Surgery in the United States and other countries.',
  code: [
    {
      system: 'http://loinc.org',
      code: '82316-1',
      display: 'Hip dysfunction and osteoarthritis outcome score for joint replacement [HOOSJR]',
    },
  ],
  item: scoredSymptomItems,
};
